<template>
  <div>
    <!-- main app vue -->
    <div class="container-fluid">
      <header>
        <div class="row">
          <div class="col-xs-12">
            <ul class="nav nav-pills nav-justified">
              <li :class="{ active: tab == 'current' }">
                <a v-link="{ path: '/orders' }"
                  >Current <span class="badge" v-text="current.length"></span
                ></a>
              </li>
              <li :class="{ active: tab == 'completed' }">
                <a v-link="{ path: '/orders/completed' }"
                  >Completed
                  <span class="badge" v-text="completed.length"></span
                ></a>
              </li>
              <li :class="{ active: tab == 'future' }">
                <a v-link="{ path: '/orders/future' }"
                  >Future <span class="badge" v-text="future.length"></span
                ></a>
              </li>
              <li v-link-active>
                <a v-link="{ path: '/item-groups', activeClass: 'active' }"
                  >Grouped Items</a
                >
              </li>
              <li v-link-active>
                <a
                  v-link="{
                    path: '/products',
                    exact: true,
                    activeClass: 'active',
                  }"
                  >Inventory</a
                >
              </li>

              <li class="icon">
                <a href="/" @click="reboot()">
                  <i class="fa fa-refresh fa-lg"></i>
                </a>
              </li>
              <li class="icon">
                <a href="javascript:void(0);" @click="confirming_logout = true">
                  <i class="fa fa-sign-out fa-lg"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </header>

      <div id="content">
        <router-view></router-view>
      </div>
    </div>

    <loader></loader>
    <logout-confirmation :open.sync="confirming_logout"></logout-confirmation>
  </div>
</template>

<script>
import { get, pick } from 'lodash';
import moment from 'moment';
import { Api, Emitter, IO, printLabels, reboot } from 'src/utilities';
import Stash, { scopedOrders } from 'src/stash';
import Loader from './Loader';
import LogoutConfirmation from 'modals/LogoutConfirmation';
import InteractsWithState from 'mixins/InteractsWithState';
import ScopesOrders from 'mixins/ScopesOrders';

export default {
  mixins: [InteractsWithState, ScopesOrders],

  ready() {
    //
    // A sort of crontab, providing a means to schedule periodic tasks
    // -------------------------------------------------------------------------
    // Fired every minute to help move orders from tab to tab, and reboot every hour
    //
    setInterval(() => {
      if (moment().seconds() !== 0) return;

      // update the time every minute. this reorganizes the orders on each tab
      this.state.time = moment();

      if (!!this.state.fulfiller.auto_print_labels) {
        printLabels(scopedOrders.current());
      }

      if (this.orderMovedFromFutureToCurrent()) {
        this.playBeep();
      }
    }, 1000);

    //
    // Tell socket.io which rooms to connect to.
    // -------------------------------------------------------------------------
    //
    const rooms = ['sfu', `sfu.stores.${this.state.store.id}`];

    this.state.departments.forEach(department =>
      rooms.push(`sfu.departments.${department.id}`)
    );

    // tell the server this SFU wants to join the store-specific room/channel.
    IO.socket.on('reconnect', () => IO.emit(null, 'join', rooms));

    // join now too!
    IO.emit(null, 'join', rooms);

    IO.on('item.modified', payload =>
      Stash.updateItem(payload.order_id, payload.id, payload)
    );
    IO.on('item.destroyed', payload =>
      Stash.removeItem(payload.order_id, payload.id)
    );
    IO.on('item.created', payload => {
      Api.get(`orders/${payload.order_id}`, {
        params: { include: 'items,cardAuthorization' },
        channel: 'background',
      })
        .then(response => Stash.setOrder(payload.order_id, response.data))
        .catch(Api.handleVisibilityError);
    });

    IO.on('reboot', reboot);

    IO.on('order.label_printed', payload => {
      const attrs = {
        label_printed_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
      };

      Stash.updateOrder(payload.id, attrs);
    });

    IO.on('order.fulfilled', payload => {
      const attrs = {
        completed_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
        status: 'completed',
      };

      Stash.updateOrder(payload.id, attrs);
    });

    IO.on('order.modified', payload => {
      Stash.updateOrder(payload.id, pick(payload, ['pickup_at']));
    });

    IO.on(['order.canceled', 'order.retracted'], payload =>
      Stash.removeOrder(payload.id)
    );

    IO.on(['order.placed', 'order.updated'], payload => {
      Api.get(`orders/${payload.id}`, {
        params: { include: 'items,cardAuthorization' },
        channel: 'background',
      })
        .then(response => {
          const wasJustPlaced = !Stash.hasOrder(payload.id);

          Stash.setOrder(payload.id, response.data);

          if (!wasJustPlaced) {
            return;
          }

          if (
            moment.utc(response.data.pickup_at).local() <
            scopedOrders.timeThreshold()
          ) {
            this.playBeep(response.data.source);
          }
        })
        .catch(Api.handleVisibilityError);
    });

    IO.on('order.geofence', payload =>
      Stash.updateOrder(payload.id, {
        nearby_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
      })
    );
  },

  data() {
    return {
      confirming_logout: false,
    };
  },

  computed: {
    tab() {
      return this.$route.tab || '';
    },

    loading() {
      return this.state.loading;
    },
  },

  methods: {
    reboot,

    orderMovedFromFutureToCurrent() {
      const limit = scopedOrders.timeThreshold();

      // See what were in the future tab before state update by applying a minor offset to the time threshold.
      const currentFutureOrders = scopedOrders
        .incomplete()
        .filter(
          o => moment.utc(o.pickup_at).local() > limit.clone().subtract(30, 's')
        );

      const nextFutureOrders = scopedOrders
        .incomplete()
        .filter(o => moment.utc(o.pickup_at).local() > limit.clone());

      if (nextFutureOrders.length < currentFutureOrders.length) {
        return true;
      }
    },

    playBeep(source = 'mobile') {
      try {
        const audio = new Audio(
          Stash.getNotificationAudioForOrderSource(source)
        );
        audio.play();
      } catch (error) {
        console.error(error);
      }
    },
  },

  components: { Loader, LogoutConfirmation },
};
</script>



// WEBPACK FOOTER //
// App.vue?e33a5380